/**
 * Experience Mode Service
 * 
 * Handles all Firebase operations for the Experience Mode tour system
 */

import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  setDoc,
  updateDoc,
  serverTimestamp,
  deleteDoc,
  Timestamp
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage } from '../firebase';
import { ExperienceSpot, TourProgress } from '../types/experienceMode.types';

class ExperienceModeService {
  private readonly SPOTS_COLLECTION = 'experienceSpots';
  private readonly PROGRESS_COLLECTION = 'tourProgress';
  private readonly STORAGE_PATH = 'experienceVideos';

  /**
   * Get all spots for a specific page
   */
  async getSpotsForPage(pageId: string): Promise<ExperienceSpot[]> {
    try {
      const q = query(
        collection(db, this.SPOTS_COLLECTION),
        where('pageId', '==', pageId),
        orderBy('order', 'asc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
          updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt,
        } as ExperienceSpot;
      });
    } catch (error) {
      console.error('Error getting spots for page:', error);
      return [];
    }
  }

  /**
   * Get all spots (for counting total)
   */
  async getAllSpots(): Promise<ExperienceSpot[]> {
    try {
      const q = query(
        collection(db, this.SPOTS_COLLECTION),
        orderBy('pageId', 'asc'),
        orderBy('order', 'asc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt,
          updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt,
        } as ExperienceSpot;
      });
    } catch (error) {
      console.error('Error getting all spots:', error);
      return [];
    }
  }

  /**
   * Get user's tour progress
   */
  async getUserProgress(userId: string): Promise<TourProgress | null> {
    try {
      const docRef = doc(db, this.PROGRESS_COLLECTION, userId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        return await this.initializeProgress(userId);
      }
      
      const data = docSnap.data();
      return {
        userId,
        ...data,
        lastSeenDate: data.lastSeenDate instanceof Timestamp ? data.lastSeenDate.toDate() : data.lastSeenDate,
        completedAt: data.completedAt instanceof Timestamp ? data.completedAt.toDate() : data.completedAt,
      } as TourProgress;
    } catch (error) {
      console.error('Error getting user progress:', error);
      return null;
    }
  }

  /**
   * Initialize progress for new user
   */
  private async initializeProgress(userId: string): Promise<TourProgress> {
    try {
      // Get total spots count
      const allSpots = await this.getAllSpots();
      
      const progress: Partial<TourProgress> = {
        userId,
        seenSpots: [],
        completedPages: [],
        totalSpots: allSpots.length,
        percentage: 0,
        isComplete: false,
      };
      
      await setDoc(doc(db, this.PROGRESS_COLLECTION, userId), {
        ...progress,
        lastSeenDate: serverTimestamp(),
      });
      
      return {
        ...progress,
        lastSeenDate: new Date(),
      } as TourProgress;
    } catch (error) {
      console.error('Error initializing progress:', error);
      throw error;
    }
  }

  /**
   * Mark spot as seen
   */
  async markSpotAsSeen(userId: string, spotId: string): Promise<void> {
    try {
      const progress = await this.getUserProgress(userId);
      if (!progress) return;
      
      // Only update if not already seen
      if (!progress.seenSpots.includes(spotId)) {
        const updatedSeenSpots = [...progress.seenSpots, spotId];
        const percentage = progress.totalSpots > 0 
          ? Math.round((updatedSeenSpots.length / progress.totalSpots) * 100)
          : 0;
        const isComplete = percentage === 100;
        
        const updateData: any = {
          seenSpots: updatedSeenSpots,
          percentage,
          isComplete,
          lastSeenDate: serverTimestamp(),
        };
        
        if (isComplete) {
          updateData.completedAt = serverTimestamp();
        }
        
        await updateDoc(doc(db, this.PROGRESS_COLLECTION, userId), updateData);
      }
    } catch (error) {
      console.error('Error marking spot as seen:', error);
    }
  }

  /**
   * Reset progress (for testing or user request)
   */
  async resetProgress(userId: string): Promise<void> {
    try {
      const allSpots = await this.getAllSpots();
      
      await updateDoc(doc(db, this.PROGRESS_COLLECTION, userId), {
        seenSpots: [],
        completedPages: [],
        totalSpots: allSpots.length,
        percentage: 0,
        isComplete: false,
        completedAt: null,
        lastSeenDate: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error resetting progress:', error);
    }
  }

  /**
   * Upload video to Firebase Storage
   */
  async uploadVideo(file: File, spotId: string): Promise<string> {
    try {
      const timestamp = Date.now();
      const filename = `${spotId}_${timestamp}_${file.name}`;
      const storageRef = ref(storage, `${this.STORAGE_PATH}/${filename}`);
      
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);
      
      return downloadUrl;
    } catch (error) {
      console.error('Error uploading video:', error);
      throw error;
    }
  }

  /**
   * Delete video from Firebase Storage
   */
  async deleteVideo(videoUrl: string): Promise<void> {
    try {
      // Extract path from URL
      const path = videoUrl.split('/o/')[1]?.split('?')[0];
      if (!path) return;
      
      const decodedPath = decodeURIComponent(path);
      const storageRef = ref(storage, decodedPath);
      
      await deleteObject(storageRef);
    } catch (error) {
      console.error('Error deleting video:', error);
    }
  }

  /**
   * Create new experience spot (admin only)
   */
  async createSpot(spot: Omit<ExperienceSpot, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const docRef = doc(collection(db, this.SPOTS_COLLECTION));
      await setDoc(docRef, {
        ...spot,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      
      // Update all users' total spots count
      await this.updateAllUsersTotalSpots();
      
      return docRef.id;
    } catch (error) {
      console.error('Error creating spot:', error);
      throw error;
    }
  }

  /**
   * Update experience spot (admin only)
   */
  async updateSpot(spotId: string, updates: Partial<ExperienceSpot>): Promise<void> {
    try {
      await updateDoc(doc(db, this.SPOTS_COLLECTION, spotId), {
        ...updates,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating spot:', error);
      throw error;
    }
  }

  /**
   * Delete experience spot (admin only)
   */
  async deleteSpot(spotId: string): Promise<void> {
    try {
      // Get spot data to delete video if exists
      const spotDoc = await getDoc(doc(db, this.SPOTS_COLLECTION, spotId));
      if (spotDoc.exists()) {
        const spotData = spotDoc.data() as ExperienceSpot;
        if (spotData.videoUrl) {
          await this.deleteVideo(spotData.videoUrl);
        }
      }
      
      // Delete spot document
      await deleteDoc(doc(db, this.SPOTS_COLLECTION, spotId));
      
      // Update all users' total spots count
      await this.updateAllUsersTotalSpots();
    } catch (error) {
      console.error('Error deleting spot:', error);
      throw error;
    }
  }

  /**
   * Update total spots count for all users
   */
  private async updateAllUsersTotalSpots(): Promise<void> {
    try {
      const allSpots = await this.getAllSpots();
      const totalSpots = allSpots.length;
      
      const progressSnapshot = await getDocs(collection(db, this.PROGRESS_COLLECTION));
      
      const updatePromises = progressSnapshot.docs.map(async (progressDoc) => {
        const data = progressDoc.data();
        const seenSpotsCount = (data.seenSpots || []).length;
        const percentage = totalSpots > 0 
          ? Math.round((seenSpotsCount / totalSpots) * 100)
          : 0;
        
        return updateDoc(progressDoc.ref, {
          totalSpots,
          percentage,
          isComplete: percentage === 100,
        });
      });
      
      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error updating all users total spots:', error);
    }
  }
}

export const experienceModeService = new ExperienceModeService();
